import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Plus, Pencil, Trash2, Clock, Wrench, Users, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Services = () => {
  const [services, setServices] = useState([]);
  const [rentalItems, setRentalItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    duration_minutes: 60,
    customer_bookable: true,
    creates_rental: false,
    linked_rental_item_id: "",
    default_rental_days: 7,
  });

  const fetchServices = async () => {
    try {
      const [svcRes, rentalRes] = await Promise.all([
        axios.get(`${API}/services?include_inactive=true`),
        axios.get(`${API}/rental-items`).catch(() => ({ data: [] })),
      ]);
      setServices(svcRes.data);
      setRentalItems(rentalRes.data || []);
    } catch (error) {
      toast.error("Failed to load services");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      // Only send the rental link when creates_rental is enabled
      if (!payload.creates_rental) {
        payload.linked_rental_item_id = null;
      } else if (!payload.linked_rental_item_id) {
        toast.error("Please select the rental item linked to this service");
        return;
      }
      if (editingService) {
        await axios.put(`${API}/services/${editingService.service_id}`, payload);
        toast.success("Service updated successfully");
      } else {
        await axios.post(`${API}/services`, payload);
        toast.success("Service created successfully");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save service");
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description || "",
      duration_minutes: service.duration_minutes,
      customer_bookable: service.customer_bookable !== false, // Default to true if not set
      creates_rental: service.creates_rental === true,
      linked_rental_item_id: service.linked_rental_item_id || "",
      default_rental_days: service.default_rental_days || 7,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm("Are you sure you want to delete this service?")) return;
    try {
      await axios.delete(`${API}/services/${serviceId}`);
      toast.success("Service deleted successfully");
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete service");
    }
  };

  const resetForm = () => {
    setEditingService(null);
    setFormData({
      name: "",
      description: "",
      duration_minutes: 60,
      customer_bookable: true,
      creates_rental: false,
      linked_rental_item_id: "",
      default_rental_days: 7,
    });
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const activeServices = services.filter(s => s.is_active);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="services-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Services</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage standard services offered to customers
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-service-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Service
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingService ? "Edit Service" : "Add New Service"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Service Name *</Label>
                <Input
                  id="name"
                  data-testid="service-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Annual Maintenance"
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  data-testid="service-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Service description..."
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="duration">Duration (minutes) *</Label>
                <Input
                  id="duration"
                  type="number"
                  data-testid="service-duration-input"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 0 })}
                  min="15"
                  step="15"
                  required
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label htmlFor="customer_bookable" className="text-base">Customer Bookable</Label>
                  <p className="text-sm text-slate-500">
                    {formData.customer_bookable 
                      ? "Customers can book this service themselves" 
                      : "Only staff can book this for customers"}
                  </p>
                </div>
                <Switch
                  id="customer_bookable"
                  data-testid="service-customer-bookable-switch"
                  checked={formData.customer_bookable}
                  onCheckedChange={(checked) => setFormData({ ...formData, customer_bookable: checked })}
                />
              </div>

              {/* Rental Bridge (Gas Rentals) */}
              <div className="rounded-lg border p-4 space-y-3 bg-amber-50/40">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="creates_rental" className="text-base">Creates a Rental</Label>
                    <p className="text-sm text-slate-500">
                      Auto-creates a rental record (e.g. gas cylinder) with return schedule when booked.
                    </p>
                  </div>
                  <Switch
                    id="creates_rental"
                    data-testid="service-creates-rental-switch"
                    checked={formData.creates_rental}
                    onCheckedChange={(checked) => setFormData({ ...formData, creates_rental: checked })}
                  />
                </div>
                {formData.creates_rental && (
                  <div className="space-y-3 pt-2 border-t border-amber-200">
                    <div>
                      <Label htmlFor="linked_rental_item">Linked Rental Item *</Label>
                      <select
                        id="linked_rental_item"
                        data-testid="linked-rental-item-select"
                        value={formData.linked_rental_item_id}
                        onChange={(e) => setFormData({ ...formData, linked_rental_item_id: e.target.value })}
                        required
                        className="mt-1 block w-full rounded-md border border-slate-300 bg-white py-2 px-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                      >
                        <option value="">Select a rental item...</option>
                        {rentalItems.map((item) => (
                          <option key={item.item_id} value={item.item_id}>
                            {item.name}{item.serial_number ? ` (${item.serial_number})` : ""} — {item.available_quantity}/{item.quantity} available
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="default_rental_days">Default Rental Duration (days)</Label>
                      <Input
                        id="default_rental_days"
                        type="number"
                        min={1}
                        step={1}
                        data-testid="default-rental-days-input"
                        value={formData.default_rental_days}
                        onChange={(e) => setFormData({ ...formData, default_rental_days: parseInt(e.target.value) || 1 })}
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Return date defaults to booking date + this many days, unless overridden.
                      </p>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-service-btn">
                  {editingService ? "Update" : "Create"} Service
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Total Services</CardTitle>
            <Wrench className="h-4 w-4 text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{services.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Active Services</CardTitle>
            <Clock className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{activeServices.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Services Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Services</CardTitle>
          <CardDescription>
            Services that can be assigned to customers for booking
          </CardDescription>
        </CardHeader>
        <CardContent>
          {services.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Wrench className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No services defined yet</p>
              <p className="text-sm">Create your first service to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Service Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {services.map((service) => (
                  <TableRow key={service.service_id} data-testid={`service-row-${service.service_id}`}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => handleEdit(service)}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {service.name}
                        {service.customer_bookable === false && (
                          <Lock className="h-3.5 w-3.5 text-slate-400" title="Internal only" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-500 max-w-xs truncate">
                      {service.description || "-"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4 text-slate-400" />
                        {formatDuration(service.duration_minutes)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant={service.is_active ? "default" : "secondary"}>
                          {service.is_active ? "Active" : "Inactive"}
                        </Badge>
                        <Badge variant={service.customer_bookable !== false ? "outline" : "secondary"} className="text-xs">
                          {service.customer_bookable !== false ? (
                            <><Users className="h-3 w-3 mr-1" />Customer</>
                          ) : (
                            <><Lock className="h-3 w-3 mr-1" />Internal</>
                          )}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(service)}
                          data-testid={`edit-service-${service.service_id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(service.service_id)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`delete-service-${service.service_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Services;
